import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { getLegalPage, LEGAL_INDEX, COMPANY } from "../data/legal";

const LOGO = "https://customer-assets.emergentagent.com/job_261f6229-25c6-462a-bf7c-d1c15ef074eb/artifacts/vpvrvwp5_logo%20imagotip%20xpro%20blanco.png";

export default function LegalPage() {
  const { slug } = useParams();
  const { lang, setLang } = useLang();
  const [activeIdx, setActiveIdx] = useState(0);

  const page = getLegalPage(slug, lang);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 90, damping: 20, mass: 0.3 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // ScrollSpy for TOC
  useEffect(() => {
    if (!page) return;
    const onScroll = () => {
      const marks = page.sections.map((_, i) => document.getElementById(`sec-${i}`));
      const y = window.scrollY + 140;
      let found = 0;
      for (let i = 0; i < marks.length; i++) {
        if (marks[i] && marks[i].offsetTop <= y) found = i;
      }
      setActiveIdx(found);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [page, slug]);

  if (!page) return <Navigate to="/" replace />;

  const updated = new Date(COMPANY.updated).toLocaleDateString(lang === "ca" ? "ca-ES" : "es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-black text-white relative overflow-x-hidden">
      {/* Scroll progress line */}
      <motion.div
        style={{ scaleX }}
        className="fixed top-0 left-0 right-0 h-[2px] bg-[#FFCC00] origin-left z-[60]"
      />

      {/* Top bar */}
      <header className="fixed top-0 inset-x-0 z-50 bg-black/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-back-home">
            <img src={LOGO} alt="XPRO Fitness" className="h-10 w-auto" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {LEGAL_INDEX.map((it) => (
              <Link
                key={it.slug}
                to={`/legal/${it.slug}`}
                data-testid={`legal-nav-${it.slug}`}
                className={`text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${
                  it.slug === slug ? "text-[#FFCC00]" : "text-white/60 hover:text-white"
                }`}
              >
                {it[lang]}
              </Link>
            ))}
          </div>

          <div className="inline-flex items-center border border-white/15 text-[12px] tracking-widest font-semibold select-none">
            <button
              onClick={() => setLang("ca")}
              className={`px-3 py-2 ${lang === "ca" ? "bg-[#FFCC00] text-black" : "text-white/70"}`}
            >
              CA
            </button>
            <button
              onClick={() => setLang("es")}
              className={`px-3 py-2 ${lang === "es" ? "bg-[#FFCC00] text-black" : "text-white/70"}`}
            >
              ES
            </button>
          </div>
        </div>
      </header>

      {/* Faded huge slug watermark */}
      <div
        aria-hidden
        className="pointer-events-none select-none absolute top-24 right-[-4rem] font-display text-[16rem] md:text-[26rem] text-white/[0.025] leading-none hidden md:block"
      >
        {slug.split("-").join(" ").slice(0, 8).toUpperCase()}
      </div>

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 md:pt-40 pb-24 md:pb-36">
        {/* Breadcrumb + back */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-2 mb-8 md:mb-10 text-[11px] tracking-[0.25em] uppercase text-white/50"
        >
          <Link to="/" className="hover:text-[#FFCC00] inline-flex items-center gap-2" data-testid="legal-breadcrumb-home">
            <ArrowLeft size={12} /> {lang === "ca" ? "Tornar a la web" : "Volver a la web"}
          </Link>
          <span className="text-white/20">/</span>
          <span>{lang === "ca" ? "Legal" : "Legal"}</span>
          <span className="text-white/20">/</span>
          <span className="text-white/80">{page.title}</span>
        </motion.div>

        {/* Hero */}
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-end mb-16 md:mb-24">
          <div className="md:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#FFCC00] mb-6"
            >
              {page.overline}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              data-testid="legal-title"
              className="font-display text-[3rem] sm:text-[4.5rem] lg:text-[6rem] leading-[0.9] text-white"
            >
              {page.title}
            </motion.h1>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="md:col-span-4 space-y-4 border-l border-white/10 pl-6"
          >
            <MetaRow k={lang === "ca" ? "Titular" : "Titular"} v={COMPANY.name} />
            <MetaRow k={lang === "ca" ? "NIF" : "NIF"} v={COMPANY.cif} />
            <MetaRow k={lang === "ca" ? "Actualitzat" : "Actualizado"} v={updated} />
          </motion.div>
        </div>

        {/* Intro + TOC + Body */}
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          {/* TOC sidebar (sticky, desktop) */}
          <aside className="hidden md:block md:col-span-3">
            <div className="sticky top-32">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#FFCC00] mb-4">
                {lang === "ca" ? "Índex" : "Índice"}
              </div>
              <ul className="space-y-2" data-testid="legal-toc">
                {page.sections.map((s, i) => (
                  <li key={i}>
                    <a
                      href={`#sec-${i}`}
                      data-testid={`legal-toc-${i}`}
                      className={`group flex items-baseline gap-3 py-1.5 text-[13px] leading-snug transition-colors ${
                        i === activeIdx ? "text-[#FFCC00]" : "text-white/55 hover:text-white"
                      }`}
                    >
                      <span
                        className={`shrink-0 font-display text-[11px] tabular-nums w-6 ${
                          i === activeIdx ? "text-[#FFCC00]" : "text-white/30"
                        }`}
                      >
                        / {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{s.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Content */}
          <div className="md:col-span-9">
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-lg md:text-2xl text-white/85 leading-relaxed font-body mb-14 md:mb-20 max-w-3xl border-l-2 border-[#FFCC00] pl-6"
            >
              {page.intro}
            </motion.p>

            <div className="space-y-16 md:space-y-24">
              {page.sections.map((s, i) => (
                <motion.section
                  key={i}
                  id={`sec-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5 }}
                  data-testid={`legal-section-${i}`}
                  className="scroll-mt-32"
                >
                  <div className="flex items-baseline gap-4 mb-5">
                    <span className="font-display text-2xl md:text-3xl text-[#FFCC00] tabular-nums">
                      / {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="h-px flex-1 bg-white/10" />
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-white leading-[0.95] mb-6 max-w-3xl">
                    {s.title}
                  </h2>
                  <div className="space-y-5 max-w-3xl text-[15px] md:text-[17px] leading-[1.75] text-white/75 font-body">
                    {s.body.map((par, j) => (
                      <p key={j}>{par}</p>
                    ))}
                  </div>
                </motion.section>
              ))}
            </div>

            {/* End marker + contact */}
            <div className="mt-24 md:mt-32 pt-10 border-t border-white/10 max-w-3xl">
              <div className="text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                {lang === "ca" ? "Fi del document" : "Fin del documento"}
              </div>
              <div className="flex items-center gap-2 text-[#FFCC00] mb-8">
                <span className="w-2 h-2 bg-[#FFCC00] rotate-45" />
                <span className="text-xs tracking-[0.25em] uppercase font-bold">
                  {lang === "ca" ? "Última actualització" : "Última actualización"} · {updated}
                </span>
              </div>
              <p className="text-white/60 text-sm mb-6">
                {lang === "ca"
                  ? `Per a qualsevol consulta sobre aquestes condicions, escriu a ${COMPANY.email} o vine al centre a Vidreres.`
                  : `Para cualquier consulta sobre estas condiciones, escribe a ${COMPANY.email} o visítanos en el centro de Vidreres.`}
              </p>
              <a
                href={`mailto:${COMPANY.email}`}
                className="inline-flex items-center gap-3 px-6 py-3 border border-white/20 text-white text-[11px] tracking-[0.22em] uppercase font-bold hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors"
                data-testid="legal-contact-email"
              >
                {COMPANY.email}
                <ArrowUpRight size={14} />
              </a>
            </div>

            {/* Cross-links */}
            <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {LEGAL_INDEX.filter((it) => it.slug !== slug).map((it) => (
                <Link
                  key={it.slug}
                  to={`/legal/${it.slug}`}
                  className="group flex items-center justify-between border border-white/10 hover:border-[#FFCC00] p-5 transition-colors"
                  data-testid={`legal-cross-${it.slug}`}
                >
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 group-hover:text-[#FFCC00] mb-1">
                      {lang === "ca" ? "Llegir" : "Leer"}
                    </div>
                    <div className="font-display text-2xl text-white group-hover:text-[#FFCC00]">{it[lang]}</div>
                  </div>
                  <ArrowUpRight size={22} className="text-white/50 group-hover:text-[#FFCC00] group-hover:rotate-45 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function MetaRow({ k, v }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-white/40 mb-1">{k}</div>
      <div className="text-sm md:text-base text-white font-body">{v}</div>
    </div>
  );
}
